import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { compose } from 'react-apollo';
import { connect } from 'react-redux';
import { connectLean } from 'lean-redux';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import ResourceAssign from 'chameleon-src/components/assign/ResourceAssign';
import getClassesByEP from 'chameleon-src/utility/getClassesByEP';
import { updateItemsWithAssignedState } from './utils';

const AssignAllVideos = (props) => {
  const {
    items: defaultItems,
    unitNumber,
    subjectData,
    isAdminOrCoordinator,
    chosenEducationPeriodCode,
    userClasses,
    resourcesState,
    persistAssignedSections,
    containerClasses,
    isButtonInverted,
    subunitTitle,
    subunitLabel,
    unitTitle,
    context,
    canRemove,
  } = props;
  const { id: subjectId } = subjectData;
  const [items, setItems] = useState(null);

  const setUpdatedItems = useCallback(() => {
    const updatedItems = updateItemsWithAssignedState(
      subjectId,
      resourcesState,
      defaultItems,
    );
    setItems(updatedItems);
  }, [subjectId, resourcesState, defaultItems]);

  useEffect(() => {
    setUpdatedItems();
  }, [defaultItems, resourcesState]);

  const currentClasses = useMemo(
    () => getClassesByEP(userClasses, chosenEducationPeriodCode),
    [userClasses, chosenEducationPeriodCode],
  );

  const onResourceDeselect = (resourceId) => {
    const itemToRemove = items.find((i) => i.id === resourceId);
    const itemToRemoveIndex = items.indexOf(itemToRemove);
    setItems([
      ...items.slice(0, itemToRemoveIndex),
      ...items.slice(itemToRemoveIndex + 1),
    ]);
  };

  const onReset = () => setUpdatedItems();

  if (!items) {
    return null;
  }

  return (
    <div className={containerClasses}>
      <ResourceAssign
        items={items}
        videoResource={{ unit: { unitId: unitNumber } }}
        subject={subjectData}
        userClasses={currentClasses}
        savedClasses={[]}
        hasNoClasses={!currentClasses.length}
        isAdminOrCoordinator={isAdminOrCoordinator}
        unitNumber={unitNumber}
        unitTitle={unitTitle}
        subunitTitle={subunitTitle}
        subunitLabel={subunitLabel}
        onSubmit={(sectionIds) => {
          const intSectionIds = sectionIds.map((value) =>
            Number.parseInt(value, 10),
          );

          persistAssignedSections(
            subjectId,
            resourcesState,
            items,
            intSectionIds,
          );
        }}
        assignButton={
          <ButtonWithIcon
            className={cn('text-white bg-purple-600 hover:bg-purple-700', {
              'border border-white': isButtonInverted,
            })}
            icon="person_add_btn"
            iconClassName="text-white"
            iconWidth={20}
            iconHeight={20}
            accessibility={{ 'aria-label': 'Assign all' }}
          >
            Assign All
          </ButtonWithIcon>
        }
        assignAll
        context={context}
        onResourceDeselect={canRemove ? onResourceDeselect : undefined}
        onReset={onReset}
      />
    </div>
  );
};

AssignAllVideos.propTypes = {
  items: PropTypes.array.isRequired,
  unitNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  isAdminOrCoordinator: PropTypes.bool,
  subjectData: PropTypes.oneOfType([PropTypes.object, PropTypes.bool])
    .isRequired,
  chosenEducationPeriodCode: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  userClasses: PropTypes.array,
  resourcesState: PropTypes.object.isRequired,
  persistAssignedSections: PropTypes.func.isRequired,
  containerClasses: PropTypes.string,
  isButtonInverted: PropTypes.bool,
  subunitTitle: PropTypes.string,
  subunitLabel: PropTypes.string.isRequired,
  unitTitle: PropTypes.string,
  context: PropTypes.string.isRequired,
  canRemove: PropTypes.bool.isRequired,
};

AssignAllVideos.defaultProps = {
  isAdminOrCoordinator: false,
  userClasses: [],
  containerClasses: '',
  isButtonInverted: false,
  subunitTitle: null,
  unitTitle: null,
};

const mapStateToProps = ({
  userData: { isAdmin, isCoordinator, chosenEducationPeriodCode },
  subject: { data: subjectData },
  classes: { items: userClasses },
  featureFlag: { assign_all_videos_remove_x: canRemove },
}) => ({
  subjectData,
  isAdminOrCoordinator: isAdmin || isCoordinator,
  chosenEducationPeriodCode,
  userClasses,
  canRemove,
});

export default compose(
  connect(mapStateToProps),
  connectLean({
    scope: ({ unitNumber }) => ['units', `unit-${unitNumber}`, 'resources'],
    mapState: (resourcesState) => ({
      resourcesState,
    }),
    persistAssignedSections(subjectId, resourcesState, items, sectionIds) {
      const resourceKeys = items.map(
        (item) => `resource-${item.resourceId}-subject-${subjectId}`,
      );
      this.setState({
        ...resourceKeys.reduce((acc, key) => {
          acc[key] = {
            ...resourcesState[key],
            savedClasses: sectionIds,
            selectedItems: sectionIds,
          };

          return acc;
        }, {}),
      });
    },
  }),
)(AssignAllVideos);
